import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Menu, X, Sparkles, LogIn, UserCog } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/exchange", label: "Exchange" },
  { to: "/availableme", label: "AvailableMe" },
  { to: "/defenders", label: "Defenders" },
  { to: "/services", label: "Services" },
  { to: "/cities", label: "Locations" },
  { to: "/intel", label: "Intel" },
  { to: "/join", label: "Join" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  return (
    <header className="bg-[#fff7e6]/85 backdrop-blur border-b border-[#e7e3da] sticky top-0 z-40" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-[68px] gap-3">
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#f43f5e] rounded-2xl flex items-center justify-center text-white shadow-[3px_3px_0_#0b1020] group-hover:rotate-[-6deg] transition-transform">
              <Sparkles className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="display-black text-[17px] text-[#0b1020]">Available<span className="text-[#f43f5e]">.</span></div>
              <div className="text-[10.5px] text-[#4b5468] font-medium hidden sm:block">Junk Removal · Anything. Anywhere.</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
            {NAV.map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `text-[14px] font-medium transition-colors ${
                    isActive ? "text-[#f43f5e]" : "text-[#0b1020] hover:text-[#1d4ed8]"
                  }`
                }
                data-testid={`nav-${n.to.replace("/", "")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop right cluster */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1d4ed8] hover:text-[#0b1020]"
                  data-testid="nav-dashboard"
                >
                  {isAdmin && <UserCog className="w-4 h-4" />}
                  {isAdmin ? "Admin" : "Dashboard"}
                </Link>
                <button onClick={() => { logout(); navigate("/"); }} className="btn-ghost" data-testid="logout-btn">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost" data-testid="nav-login">
                  <LogIn className="w-4 h-4" /> Sign in
                </Link>
                <Link to="/quote" className="btn-signal" data-testid="header-quote-btn">Get Quote →</Link>
              </>
            )}
          </div>

          {/* Mobile right cluster — Login pill always visible alongside burger */}
          <div className="flex lg:hidden items-center gap-2">
            {user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-semibold bg-[#0b1020] text-white"
                data-testid="mobile-dashboard-btn"
              >
                {isAdmin && <UserCog className="w-3.5 h-3.5" />}
                {isAdmin ? "Admin" : "Account"}
              </Link>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-semibold bg-[#0b1020] text-white"
                data-testid="mobile-login-btn"
              >
                <LogIn className="w-3.5 h-3.5" /> Sign in
              </Link>
            )}
            <button
              className="p-2"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#e7e3da] bg-[#fff7e6]" data-testid="mobile-nav">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map(n => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="text-sm font-medium text-[#0b1020]" data-testid={`mobile-nav-${n.to.replace("/", "")}`}>
                {n.label}
              </Link>
            ))}
            <div className="flex gap-2 pt-3 border-t border-[#e7e3da]">
              <Link to="/quote" onClick={() => setOpen(false)} className="btn-signal w-fit">Get Quote →</Link>
              {user ? (
                <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="btn-ghost w-fit">Logout</button>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost w-fit">
                  <LogIn className="w-4 h-4" /> Sign in
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
